import React, { useState, useEffect, useMemo, useCallback } from "react";
import API_BASE_URL from "../config";

const TANK_LOCATIONS = ["Main Tank", "Reserve Tank", "Tank 1", "Tank 2", "Tank 3"];
const SHIFTS = ["Morning", "Afternoon", "Night"];

const SHIFT_CONFIG = {
  Morning: { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  Afternoon: { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  Night: { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" },
};

const emptyLog = () => ({
  plateNumber: "",
  truckDescription: "",
  dipstickReading: "",
  litersRemaining: "",
  tankLocation: "Main Tank",
  tankCapacity: "",
  shift: "Morning",
  logDate: new Date().toISOString().split("T")[0],
  remarks: "",
});

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={onCancel} />
      <div style={{ position: "relative", zIndex: 10, background: "#fff", borderRadius: 16, width: "min(400px,92vw)", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", overflow: "hidden", fontFamily: "sans-serif", border: "1px solid #e5e7eb" }}>
        <div style={{ background: "#1d4ed8", padding: "14px 20px", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="16" height="16" fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{title}</span>
        </div>
        <div style={{ padding: "20px" }}>
          <p style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{message}</p>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "6px 16px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontWeight: 500, color: "#6b7280", background: "#fff", cursor: "pointer" }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: "6px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#fff", background: "#ef4444", cursor: "pointer" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Bar Gauge ─────────────────────────────────────────────────────────────────
function BarGauge({ value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = pct < 20 ? "#ef4444" : pct < 50 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ width: "100%", marginTop: 4 }}>
      <div style={{ height: 5, background: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width .5s" }} />
      </div>
    </div>
  );
}

// ─── Stock Badge ───────────────────────────────────────────────────────────────
function StockBadge({ liters, capacity }) {
  const pct = capacity > 0 ? (liters / capacity) * 100 : 100;
  if (pct <= 0) return <span style={{ background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, border: "1px solid #fca5a5" }}>Out of stock</span>;
  if (pct < 20) return <span style={{ background: "#fef9c3", color: "#854d0e", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, border: "1px solid #fde047" }}>⚠ Low</span>;
  return <span style={{ background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, border: "1px solid #86efac" }}>In stock</span>;
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function FuelMonitor() {
  const [fuelLogs, setFuelLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [logForm, setLogForm] = useState(emptyLog());
  const [expandedDays, setExpandedDays] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelLog`);
      if (res.ok) {
        const data = await res.json();
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated))
          : [];
        setFuelLogs(sorted);
        if (sorted.length > 0) {
          const latestDay = (sorted[0].logDate || sorted[0].dateCreated)?.split("T")[0];
          if (latestDay) setExpandedDays({ [latestDay]: true });
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const stats = useMemo(() => {
    if (!fuelLogs.length) return { latestReading: 0, latestCapacity: 0, weekConsumed: 0, totalEntries: 0, avgDaily: 0 };
    const latest = fuelLogs[0];
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 6);
    const weekLogs = fuelLogs.filter(l => new Date(l.logDate || l.dateCreated) >= weekAgo);
    const weekConsumed = weekLogs.reduce((s, l) => s + (Number(l.litersConsumed) || 0), 0);
    const days = new Set(weekLogs.map(l => (l.logDate || l.dateCreated)?.split("T")[0])).size || 1;
    return {
      latestReading: Number(latest.litersRemaining) || 0,
      latestCapacity: Number(latest.tankCapacity) || 0,
      weekConsumed,
      totalEntries: fuelLogs.length,
      avgDaily: weekConsumed / days,
    };
  }, [fuelLogs]);

  const groupedByDay = useMemo(() => {
    const map = {};
    fuelLogs.forEach(log => {
      const day = (log.logDate || log.dateCreated)?.split("T")[0];
      if (!day) return;
      if (!map[day]) map[day] = [];
      map[day].push(log);
    });
    return Object.entries(map)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, logs]) => ({
        date, logs,
        minRemaining: Math.min(...logs.map(l => Number(l.litersRemaining) || 0)),
        totalConsumed: logs.reduce((s, l) => s + (Number(l.litersConsumed) || 0), 0),
      }));
  }, [fuelLogs]);

  const handleSubmit = async () => {
    if (!logForm.litersRemaining) {
      showToast("Liters remaining is required.", "error");
      return;
    }

    // ✅ Find the most recent log BEFORE the selected date (same tank)
    const selectedDate = new Date(logForm.logDate + "T23:59:59");

    const prevLog = fuelLogs
      .filter(l => {
        const logDate = new Date(l.logDate || l.dateCreated);
        return (
          l.tankLocation === logForm.tankLocation &&
          logDate < selectedDate
        );
      })
      .sort((a, b) =>
        new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated)
      )[0];

    const litersConsumed = prevLog
      ? Math.max(0, Number(prevLog.litersRemaining) - parseFloat(logForm.litersRemaining))
      : 0;

    // ✅ Days gap calculation (para malaman kung ilang araw na gap)
    const dayGap = prevLog
      ? Math.round(
        (selectedDate - new Date(prevLog.logDate || prevLog.dateCreated)) /
        (1000 * 60 * 60 * 24)
      )
      : null;

    setSubmitting(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const payload = {
        plateNumber: logForm.plateNumber || null,
        truckDescription: logForm.truckDescription || null,
        dipstickReading: parseFloat(logForm.dipstickReading) || 0,
        litersRemaining: parseFloat(logForm.litersRemaining),
        litersConsumed,
        dayGap,           // ✅ i-save din para sa reference
        tankLocation: logForm.tankLocation,
        tankCapacity: logForm.tankCapacity ? parseFloat(logForm.tankCapacity) : null,
        shift: logForm.shift,
        logDate: new Date(logForm.logDate).toISOString(),
        remarks: logForm.remarks || null,
        loggedBy: `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || "System",
        loggedByUserId: user.id || user.userId,
      };

      const res = await fetch(`${API_BASE_URL}api/FuelLog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save.");

      // ✅ Toast with consumed info
      if (litersConsumed > 0 && dayGap !== null) {
        showToast(
          `Saved! ${litersConsumed.toFixed(1)}L consumed over ${dayGap} day${dayGap !== 1 ? "s" : ""}.`,
          "success"
        );
      } else {
        showToast("Fuel log saved!", "success");
      }

      setShowForm(false);
      setLogForm(emptyLog());
      fetchLogs();

    } catch (err) {
      showToast(err.message || "Save failed.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async (id) => {
    setConfirm(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelLog/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setFuelLogs(prev => prev.filter(l => l.id !== id));
      showToast("Entry deleted.");
    } catch { showToast("Delete failed.", "error"); }
  };

  const toggleDay = (date) => setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));
  const today = new Date().toISOString().split("T")[0];

  const inputCls = { width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#111827", background: "#f9fafb", outline: "none", boxSizing: "border-box", fontFamily: "sans-serif" };
  const selectCls = { ...inputCls, cursor: "pointer" };

  return (
    <>
      <style>{`
        @keyframes fm-spin { to { transform: rotate(360deg) } }
        @keyframes fm-toast { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
        @keyframes fm-slide { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        .fm-del-btn { color: #d1d5db; background: transparent; border: none; cursor: pointer; padding: 4px; border-radius: 6px; display:flex; align-items:center; }
        .fm-del-btn:hover { color: #ef4444; background: #fee2e2; }
        .fm-day-row:hover { background: #f9fafb; }
        .fm-log-row:hover { background: #f9fafb; }
      `}</style>

      <ConfirmModal
        open={!!confirm}
        title="Delete Log Entry"
        message="This action cannot be undone. The entry will be permanently removed."
        onConfirm={() => doDelete(confirm.id)}
        onCancel={() => setConfirm(null)}
      />

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 500, animation: "fm-toast .25s ease", background: toast.type === "error" ? "#fee2e2" : "#dcfce7", border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`, color: toast.type === "error" ? "#b91c1c" : "#166534", padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
          {toast.type === "error"
            ? <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            : <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {toast.msg}
        </div>
      )}

      <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "sans-serif", padding: "24px 20px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── MAIN CARD ── */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>

            {/* Title bar */}
            <div style={{ background: "#1d4ed8", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>Fuel Monitor</span>
                <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, padding: "2px 10px", borderRadius: 99 }}>Dipstick Tracking</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.15)", borderRadius: 99, padding: "3px 10px" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", display: "inline-block" }} />
                  <span style={{ color: "#fff", fontSize: 11, fontWeight: 600 }}>LIVE</span>
                </div>
                <button onClick={fetchLogs} disabled={loading} style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "5px 12px", color: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                  <svg style={{ animation: loading ? "fm-spin .8s linear infinite" : "none", width: 13, height: 13 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Refresh
                </button>
                <button onClick={() => setShowForm(v => !v)} style={{ display: "flex", alignItems: "center", gap: 5, background: showForm ? "rgba(239,68,68,0.2)" : "#fff", border: showForm ? "1px solid rgba(239,68,68,0.4)" : "1px solid #fff", borderRadius: 8, padding: "5px 14px", color: showForm ? "#fca5a5" : "#1d4ed8", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  {showForm
                    ? <><svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Close</>
                    : <><svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> Log Dipstick</>
                  }
                </button>
              </div>
            </div>

            {/* ── STAT CARDS ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
              {[
                { label: "Latest Reading", value: `${stats.latestReading.toLocaleString()} L`, icon: <svg width="16" height="16" fill="none" stroke="#1d4ed8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, iconBg: "#dbeafe", valColor: "#1d4ed8" },
                { label: "Weekly Consumed", value: `${stats.weekConsumed.toFixed(1)} L`, icon: <svg width="16" height="16" fill="none" stroke="#b45309" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /></svg>, iconBg: "#fef3c7", valColor: "#b45309" },
                { label: "Daily Average", value: `${stats.avgDaily.toFixed(1)} L/day`, icon: <svg width="16" height="16" fill="none" stroke="#7c3aed" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, iconBg: "#ede9fe", valColor: "#7c3aed" },
                { label: "Total Entries", value: stats.totalEntries, icon: <svg width="16" height="16" fill="none" stroke="#065f46" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>, iconBg: "#d1fae5", valColor: "#065f46" },
              ].map((s, i) => (
                <div key={i} style={{ background: "#f9fafb", border: "1px solid #f3f4f6", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</span>
                    <div style={{ width: 28, height: 28, background: s.iconBg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</div>
                  </div>
                  <p style={{ color: s.valColor, fontSize: 18, fontWeight: 700, margin: 0, fontFamily: "monospace" }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* ── LOG FORM ── */}
            {showForm && (
              <div style={{ margin: "16px 20px", border: "1px solid #bfdbfe", borderRadius: 12, padding: "20px", background: "#eff6ff", animation: "fm-slide .2s ease" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <svg width="15" height="15" fill="none" stroke="#1d4ed8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  <span style={{ color: "#1e40af", fontWeight: 600, fontSize: 13 }}>New Dipstick Entry</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
                  {[
                    { label: "Log Date", key: "logDate", type: "date" },
                    { label: "Dipstick Reading (cm)", key: "dipstickReading", type: "number", ph: "e.g. 45.5" },
                    { label: "Liters Remaining ✱", key: "litersRemaining", type: "number", ph: "e.g. 150.00", accent: true },
                    { label: "Tank Capacity (L)", key: "tankCapacity", type: "number", ph: "optional" },
                    { label: "Plate Number", key: "plateNumber", ph: "optional" },
                    { label: "Remarks", key: "remarks", ph: "optional notes..." },
                  ].map(({ label, key, type, ph, accent }) => (
                    <div key={key}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: accent ? "#1d4ed8" : "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>{label}</label>
                      <input type={type || "text"} value={logForm[key]} placeholder={ph}
                        onChange={e => setLogForm(p => ({ ...p, [key]: e.target.value }))}
                        style={{ ...inputCls, borderColor: accent ? "#93c5fd" : "#e5e7eb", background: accent ? "#eff6ff" : "#fff" }} />
                    </div>
                  ))}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Shift</label>
                    <select value={logForm.shift} onChange={e => setLogForm(p => ({ ...p, shift: e.target.value }))} style={selectCls}>
                      {SHIFTS.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>Tank Location</label>
                    <select value={logForm.tankLocation} onChange={e => setLogForm(p => ({ ...p, tankLocation: e.target.value }))} style={selectCls}>
                      {TANK_LOCATIONS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                {logForm.litersRemaining && (() => {
                  const selectedDate = new Date(logForm.logDate + "T23:59:59");

                  // ✅ Same logic — pinaka-recent bago sa selected date
                  const prev = fuelLogs
                    .filter(l => {
                      const logDate = new Date(l.logDate || l.dateCreated);
                      return (
                        l.tankLocation === logForm.tankLocation &&
                        logDate < selectedDate
                      );
                    })
                    .sort((a, b) =>
                      new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated)
                    )[0];

                  if (!prev) return null;

                  const consumed = Math.max(
                    0,
                    Number(prev.litersRemaining) - parseFloat(logForm.litersRemaining)
                  );
                  const dayGap = Math.round(
                    (selectedDate - new Date(prev.logDate || prev.dateCreated)) /
                    (1000 * 60 * 60 * 24)
                  );

                  return (
                    <div style={{ marginTop: 12, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" fill="none" stroke="#b45309" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                      </svg>
                      <span style={{ color: "#92400e", fontSize: 12, fontWeight: 600 }}>
                        Estimated consumed from last <strong>{logForm.tankLocation}</strong> log
                        ({dayGap} day{dayGap !== 1 ? "s" : ""} ago): <strong>{consumed.toFixed(1)} L</strong>
                      </span>
                    </div>
                  );
                })()}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid #bfdbfe" }}>
                  <button onClick={() => setShowForm(false)} style={{ padding: "6px 16px", border: "1px solid #e5e7eb", borderRadius: 8, color: "#6b7280", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: "inherit" }}>Cancel</button>
                  <button onClick={handleSubmit} disabled={submitting} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 18px", background: "#1d4ed8", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1, fontFamily: "inherit" }}>
                    {submitting && <svg style={{ animation: "fm-spin .7s linear infinite", width: 13, height: 13 }} fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} /><path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} /></svg>}
                    {submitting ? "Saving…" : "Save Log"}
                  </button>
                </div>
              </div>
            )}

            {/* ── LOG TABLE ── */}
            <div>
              {/* Table title bar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6", background: "#f9fafb" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="14" height="14" fill="none" stroke="#6b7280" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  <span style={{ color: "#374151", fontWeight: 600, fontSize: 13 }}>Dipstick Log History</span>
                  <span style={{ background: "#dbeafe", color: "#1e40af", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 99, border: "1px solid #bfdbfe" }}>{fuelLogs.length} entries</span>
                </div>
              </div>

              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "60px", color: "#9ca3af" }}>
                  <svg style={{ animation: "fm-spin .8s linear infinite", width: 18, height: 18 }} fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} /><path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} /></svg>
                  <span style={{ fontSize: 13 }}>Loading logs…</span>
                </div>
              ) : groupedByDay.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px", gap: 10, color: "#9ca3af" }}>
                  <svg width="40" height="40" fill="none" stroke="#d1d5db" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  <p style={{ fontSize: 13, fontWeight: 500, color: "#9ca3af", margin: 0 }}>No logs yet — click "Log Dipstick" to start</p>
                </div>
              ) : (
                <div>
                  {/* Column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "40px 120px 110px 90px 130px 110px 1fr 40px", gap: 0, padding: "8px 20px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                    {["#", "Shift", "Tank", "Dipstick", "Liters Rem.", "Consumed", "Logged By / Remarks", ""].map(h => (
                      <span key={h} style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</span>
                    ))}
                  </div>

                  {groupedByDay.map(({ date, logs, minRemaining, totalConsumed }) => {
                    const isExpanded = expandedDays[date];
                    const isToday = date === today;
                    const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });

                    return (
                      <div key={date} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        {/* Day row */}
                        <button className="fm-day-row" onClick={() => toggleDay(date)}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "background .15s" }}>
                          <svg style={{ transition: "transform .2s", transform: isExpanded ? "rotate(90deg)" : "none", color: "#9ca3af", flexShrink: 0 }} width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 200 }}>
                            <svg width="13" height="13" fill="none" stroke="#1d4ed8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            <span style={{ color: "#374151", fontWeight: 600, fontSize: 13 }}>{dateLabel}</span>
                            {isToday && <span style={{ background: "#1d4ed8", color: "#fff", fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 99 }}>TODAY</span>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 20, marginLeft: "auto" }}>
                            <div style={{ textAlign: "right" }}>
                              <p style={{ color: "#1d4ed8", fontSize: 12, fontWeight: 700, fontFamily: "monospace", margin: 0 }}>{minRemaining.toFixed(1)} L</p>
                              <p style={{ color: "#9ca3af", fontSize: 10, margin: "1px 0 0", textTransform: "uppercase", fontWeight: 600 }}>min rem.</p>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <p style={{ color: "#b45309", fontSize: 12, fontWeight: 700, fontFamily: "monospace", margin: 0 }}>-{totalConsumed.toFixed(1)} L</p>
                              <p style={{ color: "#9ca3af", fontSize: 10, margin: "1px 0 0", textTransform: "uppercase", fontWeight: 600 }}>consumed</p>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <p style={{ color: "#6b7280", fontSize: 12, fontWeight: 700, margin: 0 }}>{logs.length}</p>
                              <p style={{ color: "#9ca3af", fontSize: 10, margin: "1px 0 0", textTransform: "uppercase", fontWeight: 600 }}>entries</p>
                            </div>
                          </div>
                        </button>

                        {/* Expanded log rows */}
                        {isExpanded && logs.map((log, i) => {
                          const sc = SHIFT_CONFIG[log.shift] || SHIFT_CONFIG.Morning;
                          const remaining = Number(log.litersRemaining);
                          const capacity = Number(log.tankCapacity) || 0;
                          const consumed = Number(log.litersConsumed) || 0;
                          return (
                            <div key={log.id} className="fm-log-row"
                              style={{ display: "grid", gridTemplateColumns: "40px 120px 110px 90px 130px 110px 1fr 40px", gap: 0, padding: "10px 20px", borderTop: "1px solid #f9fafb", alignItems: "center", background: "#fff" }}>
                              <span style={{ color: "#d1d5db", fontSize: 12, fontFamily: "monospace" }}>{i + 1}</span>
                              <div>
                                <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99 }}>{log.shift}</span>
                              </div>
                              <div>
                                <span style={{ background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb", fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 6 }}>{log.tankLocation || "—"}</span>
                              </div>
                              <span style={{ color: "#374151", fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>
                                {log.dipstickReading ?? "—"}<span style={{ color: "#9ca3af", fontSize: 11 }}> cm</span>
                              </span>
                              <div>
                                <span style={{ color: remaining < 50 ? "#ef4444" : "#1d4ed8", fontFamily: "monospace", fontSize: 15, fontWeight: 700 }}>
                                  {remaining.toFixed(1)}<span style={{ fontSize: 11, fontWeight: 500 }}> L</span>
                                </span>
                                {capacity > 0 && <BarGauge value={remaining} max={capacity} />}
                                <StockBadge liters={remaining} capacity={capacity} />
                              </div>
                              <div>
                                {consumed > 0
                                  ? <span style={{ color: "#b45309", fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>-{consumed.toFixed(1)} L</span>
                                  : <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                                }
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 500 }}>{log.loggedBy || "—"}</span>
                                {log.remarks && <span style={{ color: "#9ca3af", fontSize: 11 }}>{log.remarks}</span>}
                              </div>
                              <button className="fm-del-btn" onClick={() => setConfirm({ id: log.id })} aria-label="Delete log">
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          );
                        })}

                        {/* Day footer */}
                        {isExpanded && (
                          <div style={{ display: "flex", gap: 20, padding: "8px 20px", background: "#f0f9ff", borderTop: "1px solid #bae6fd" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: ".06em" }}>Day Summary</span>
                            <span style={{ fontSize: 12, color: "#0369a1", fontWeight: 700, fontFamily: "monospace" }}>Min: {minRemaining.toFixed(1)} L</span>
                            <span style={{ fontSize: 12, color: "#b45309", fontWeight: 700, fontFamily: "monospace" }}>Consumed: {totalConsumed.toFixed(1)} L</span>
                            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{logs.length} {logs.length === 1 ? "entry" : "entries"}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}